"use client";

import { createContext, useCallback, useContext, useRef, useState } from "react";
import type { StartupRecord } from "@/lib/types";
import type { ScoreGameInputs, TenKGameInputs } from "@/components/dashboard/metrics-games/types";

type TenKGameContextValue = {
  tenKInputs: TenKGameInputs;
  setTenKInputs: (inputs: TenKGameInputs | ((prev: TenKGameInputs) => TenKGameInputs)) => void;
  selectedStartupForTenK: StartupRecord | null;
  setSelectedStartupForTenK: (startup: StartupRecord | null) => void;
  selectedStartupForOpportunityScore: StartupRecord | null;
  setSelectedStartupForOpportunityScore: (startup: StartupRecord | null) => void;
  registerOpportunityScoreLoader: (fn: (inputs: ScoreGameInputs) => void) => () => void;
  loadOpportunityScoreInputs: (inputs: ScoreGameInputs) => void;
  /** When set, search and "Load random" are restricted to this category only. */
  categoryFilter?: string;
};

const TenKGameContext = createContext<TenKGameContextValue | null>(null);

const DEFAULT_TEN_K_INPUTS: TenKGameInputs = {
  currentMrr: 2_200,
  startupAgeMonths: 6,
  marketMedianGrowthPerMonth: 430,
  nicheMedianMrr: 4_300,
  nicheTopQuartileMrr: 9_600,
  nicheMedianArpu: 62,
  sampleSize: 14,
  arpuOverride: 0
};

type TenKGameProviderProps = {
  children: React.ReactNode;
  /** Optional initial inputs (e.g. from a real benchmark) to pre-populate the game. */
  initialTenKInputs?: Partial<TenKGameInputs>;
  /** When set, search and "Load random" are restricted to this category slug only. */
  categoryFilter?: string;
};

export function TenKGameProvider({ children, initialTenKInputs, categoryFilter }: TenKGameProviderProps) {
  const [tenKInputs, setTenKInputs] = useState<TenKGameInputs>({
    ...DEFAULT_TEN_K_INPUTS,
    ...initialTenKInputs
  });
  const [selectedStartupForTenK, setSelectedStartupForTenK] = useState<StartupRecord | null>(null);
  const [selectedStartupForOpportunityScore, setSelectedStartupForOpportunityScore] = useState<StartupRecord | null>(null);
  const opportunityScoreLoaderRef = useRef<((inputs: ScoreGameInputs) => void) | null>(null);

  const registerOpportunityScoreLoader = useCallback((fn: (inputs: ScoreGameInputs) => void) => {
    opportunityScoreLoaderRef.current = fn;
    return () => {
      opportunityScoreLoaderRef.current = null;
    };
  }, []);

  const loadOpportunityScoreInputs = useCallback((inputs: ScoreGameInputs) => {
    opportunityScoreLoaderRef.current?.(inputs);
  }, []);

  const value: TenKGameContextValue = {
    tenKInputs,
    setTenKInputs,
    selectedStartupForTenK,
    setSelectedStartupForTenK,
    selectedStartupForOpportunityScore,
    setSelectedStartupForOpportunityScore,
    registerOpportunityScoreLoader,
    loadOpportunityScoreInputs,
    categoryFilter
  };

  return (
    <TenKGameContext.Provider value={value}>
      {children}
    </TenKGameContext.Provider>
  );
}

export function useTenKGame() {
  const ctx = useContext(TenKGameContext);
  return ctx;
}
